// src/components/PDFViewer/PDFViewer.js
import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from "react";
import HeaderPDF from "./HeaderPDF";
import {
  createProjectAPI,
  getProjectDetailsAPI,
} from "../../utils/api";
import {
  ProjectContext,
  PdfHighlightContext,
} from "../../context/ProjectProvider";
import { Document, Page } from "react-pdf";
import "./PDFViewer.css";

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

/* ---------- Página con highlights ---------------------------------- */
const PageWithHighlights = React.forwardRef(
  ({ pageNumber, scale, hlRects }, ref) => {
    const [viewport, setViewport] = useState(null);
    const containerRef = useRef(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    /* Calcula desplazamiento del <canvas> dentro del contenedor */
    useEffect(() => {
      if (!viewport || !containerRef.current) return;
      const canvas = containerRef.current.querySelector("canvas");
      if (!canvas) return;
      const rectCanvas = canvas.getBoundingClientRect();
      const rectParent = containerRef.current.getBoundingClientRect();
      setOffset({
        x: rectCanvas.left - rectParent.left,
        y: rectCanvas.top - rectParent.top,
      });
    }, [viewport, scale]);

    const getRectStyle = (r, vp, curScale, off) => {
      if (!vp) return {};
      const base = r.relative
        ? {
            left: r.x0 * vp.width * curScale,
            top: r.top * vp.height * curScale,
            width: (r.x1 - r.x0) * vp.width * curScale,
            height: (r.bottom - r.top) * vp.height * curScale,
          }
        : {
            left: r.x0 * curScale,
            top: r.top * curScale,
            width: (r.x1 - r.x0) * curScale,
            height: (r.bottom - r.top) * curScale,
          };
      return {
        ...base,
        left: base.left + off.x,
        top: base.top + off.y,
      };
    };

    return (
      <div
        ref={(el) => {
          containerRef.current = el;
          if (ref) ref(el);
        }}
        style={{ position: "relative", marginBottom: "1rem" }}
      >
        <Page
          pageNumber={pageNumber}
          scale={scale}
          renderTextLayer
          renderAnnotationLayer
          onRenderSuccess={(pdfPage) =>
            setViewport(pdfPage.getViewport({ scale: 1 }))
          }
          className="pdf-page"
        />
        {viewport &&
          hlRects
            .filter((r) => r.page === undefined || r.page === pageNumber)
            .map((r, i) => (
              <div
                /* un rectángulo por highlight */
                key={i}
                className={`pdf-hl ${
                  r.kind === "center" ? "center" : "context"
                }`}
                style={{
                  position: "absolute",
                  pointerEvents: "none",
                  ...getRectStyle(r, viewport, scale, offset),
                }}
              />
            ))}
      </div>
    );
  }
);

/* ---------- Componente principal ----------------------------------- */
export default function PDFViewer() {
  const {
    /* contexto global */
    setProjects,
    setCenterCollapsed,

    loadProjectDetails,
    selectedProject,
    currentProject,
    setCurrentProject,
    isPdfColumnCollapsed,
    setPdfColumnCollapsed,
    /* flags de carga globales */
    isPdfLoading,   // descarga iniciada por ProjectProvider
    isUploadingPdf, // subida iniciada por HeaderProject
    setUploadingPdf,
  } = useContext(ProjectContext);

  const { task, clear } = useContext(PdfHighlightContext);

  /* flags locales (drag-and-drop o descarga propia) */
  const [localUploading, setLocalUploading] = useState(false);
  const [localDownloading, setLocalDownloading] = useState(false);

  /* PDF en base64 y rendering */
  const [pdfBase64, setPdfBase64] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [hlRects, setHlRects] = useState([]);

  /* ---------- Subida directa (drag & drop) ------------------------- */
  const handleFile = async (file) => {
    if (!file || file.type !== "application/pdf") return;
    setLocalUploading(true);
    setUploadingPdf(true);
    try {
      const { project } = await createProjectAPI({ name: file.name, file });
      setProjects((prev) => [...prev, project]);
      await loadProjectDetails(project.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setLocalUploading(false);
      setUploadingPdf(false);
    }
  };
 

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    handleFile(file);
  };

  /* ---------- Drag & drop handlers --------------------------------- */
  const fileInputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  /* ---------- Carga de PDF cuando cambia selectedProject ----------- */
  const reqRef = useRef(0);
  useEffect(() => {
    clear();         // limpia highlights
    setPage(1);

    if (!selectedProject) {
      setPdfBase64(null);
      return;
    }

    /* 1️⃣  ¿Ya tenemos el PDF en contexto? */
    if (
      currentProject?.id === selectedProject.id &&
      currentProject.pdf_base64
    ) {
      setPdfBase64(currentProject.pdf_base64);
      setPdfColumnCollapsed(false);
      setCenterCollapsed(false);
      return;
    }

    /* 2️⃣  Si no, lo descargamos aquí */
    const tag = ++reqRef.current;
    setCenterCollapsed(true);
    setPdfColumnCollapsed(true);
    setLocalDownloading(true);

    (async () => {
      try {
        const { project } = await getProjectDetailsAPI(selectedProject.id);
        if (reqRef.current !== tag) return;
        setCurrentProject(project);
        setPdfBase64(project.pdf_base64 ?? null);
      } catch (err) {
        if (reqRef.current === tag) setPdfBase64(null);
        console.error(err);
      } finally {
        if (reqRef.current === tag) {
          setLocalDownloading(false);
          setPdfColumnCollapsed(false);
          setCenterCollapsed(false);
        }
      }
    })();
  }, [
    selectedProject,
    currentProject,
    setCenterCollapsed,
    setCurrentProject,
    clear,
    setPdfColumnCollapsed,
  ]);

  /* Memo para la URL base64 */
  const fileSpec = useMemo(
    () => pdfBase64 && `data:application/pdf;base64,${pdfBase64}`,
    [pdfBase64]
  );

  /* ---------- Zoom -------------------------------------------------- */
  const zoomIn = () =>
    setScale((s) => Math.min(MAX_SCALE, +(s + 0.25).toFixed(2)));
  const zoomOut = () =>
    setScale((s) => Math.max(MIN_SCALE, +(s - 0.25).toFixed(2)));

  /* ---------- Referencias por página para scroll -------------------- */
  const pageRefs = useRef([]);
  useEffect(() => {
    if (task) {
      setPage(task.page);
      setHlRects(task.rects || []);
      const target = pageRefs.current[task.page - 1];
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      setHlRects([]);
    }
  }, [task]);

  /* ---------- Barra de herramientas -------------------------------- */
  const toolbar =
    fileSpec && (
      <div className="toolbar">
        <input
          type="number"
          className="form-control form-control-sm"
          value={page}
          min={1}
          max={numPages || 1}
          onChange={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val)) {
              setPage(val);
              const target = pageRefs.current[val - 1];
              target?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }}
          style={{ width: 50, textAlign: "center" }}
        />
        <span style={{ marginLeft: 4 }}>/ {numPages ?? "—"}</span>
        <div className="vr mx-2" />
        <button
          className="btn btn-light btn-sm"
          disabled={scale <= MIN_SCALE}
          onClick={zoomOut}
        >
          <i className="bi bi-zoom-out" />
        </button>
        <span>{Math.round(scale * 100)}%</span>
        <button
          className="btn btn-light btn-sm"
          disabled={scale >= MAX_SCALE}
          onClick={zoomIn}
        >
          <i className="bi bi-zoom-in" />
        </button>
      </div>
    );

  /* ---------- Flags combinados para el loader ----------------------- */
  const showLoader =
    isUploadingPdf || localUploading || isPdfLoading || localDownloading;
  const loaderText =
    isUploadingPdf || localUploading ? "Loading..." : "Downloading...";

  /* ---------- Render ------------------------------------------------ */
  return (
    <div className="group-container pdf-viewer">
      <HeaderPDF toolbar={toolbar} />

      {/* input oculto para subir PDF */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* barra de progreso */}
      {showLoader && (
        <div className="progress mb-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: "100%" }}
            aria-valuenow={100}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {loaderText}
          </div>
        </div>
      )}

      {/* PDF o drop-zone */}
      {fileSpec ? (
        <div
          className={`pdf-canvas-wrapper ${
            isPdfColumnCollapsed ? "collapsed" : ""
          }`}
        >
          <Document
            file={fileSpec}
            loading={<p className="text-muted">Rendering PDF...</p>}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={console.error}
          >
            {Array.from(new Array(numPages), (_, i) => (
              <PageWithHighlights
                key={i}
                pageNumber={i + 1}
                scale={scale}
                hlRects={hlRects}
                ref={(el) => (pageRefs.current[i] = el)}
              />
            ))}
          </Document>
        </div>
      ) : (
        !showLoader && (
          <div
            className={`pdf-drop-zone${dragOver ? " drag-over" : ""}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <p>Drag your PDF here</p>
          </div>
        )
      )}
    </div>
  );
}
